import EditableField from 'common/components/EditableField'
import { fNumber } from 'common/utils/formatNumber'
import { errorMessageByField } from 'common/utils/hook-form'
import round from 'lodash/round'
import { useIsShrimpSpecie, useSpeciesName } from 'modules/pond/api/selector'
import { SpeciesName } from 'modules/pond/constants'
import { ShrimpCategoryRecord } from 'modules/shrimpSales/types'
import {
  approxLabel,
  buildSaleUnitConverter,
  defaultUnit,
  fromKg,
  fromPricePerKg,
  SaleQuantityUnit,
  SaleUnitConverter,
  toKg,
  toPricePerKg,
} from 'modules/shrimpSales/utils/saleUnit'
import { ReactNode, useEffect, useState } from 'react'
import { Control, Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import { AddButton, AddCategoryButton } from './styled'

interface GridTableProps {
  isDialog?: boolean
  isEdit?: boolean
}

type UnitConvertFn = (
  value: number,
  unit: SaleQuantityUnit,
  converter: SaleUnitConverter
) => number | null

interface ConvertedFieldProps {
  name: string
  unit: SaleQuantityUnit
  converter: SaleUnitConverter
  control: Control<any>
  placeholder: string
  error?: ReactNode
  disabled: boolean
  /** แปลงค่าที่กรอก (หน่วยที่เลือก) → ค่าจริงที่เก็บใน form (กก. / บาท/กก.) */
  toStored: UnitConvertFn
  /** แปลงค่าจริงที่เก็บใน form → ค่าที่แสดงในหน่วยที่เลือก */
  fromStored: UnitConvertFn
  endAdornment: ReactNode
}

// เก็บ "ข้อความที่กำลังพิมพ์" แยกจากค่าที่แปลงกลับจาก field.value เสมอ
// เพราะถ้า derive ค่าที่แสดงจาก field.value ตรง ๆ ทุกครั้ง จุดทศนิยมที่พิมพ์ค้าง
// ท้าย (เช่น "10.") จะถูกปัดหายไปทันทีทุก keystroke ทำให้พิมพ์เลขทศนิยมไม่ได้เลย
// ใช้ทั้งช่องจำนวน (เก็บเป็น กก.) และช่องราคา (เก็บเป็น บาท/กก.) — form state
// จึงเป็นหน่วยเดียวกับที่ส่ง backend เสมอ ไม่ว่าผู้ใช้จะเลือกหน่วยไหน
const ConvertedField = ({
  name,
  unit,
  converter,
  control,
  placeholder,
  error,
  disabled,
  toStored,
  fromStored,
  endAdornment,
}: ConvertedFieldProps) => {
  const [draft, setDraft] = useState<string | null>(null)

  useEffect(() => {
    setDraft(null)
  }, [unit])

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const stored = Number(field.value) || 0
        const converted =
          field.value === '' || field.value === undefined
            ? null
            : fromStored(stored, unit, converter)
        const derivedValue = converted === null ? field.value ?? '' : String(round(converted, 2))
        const displayValue = draft ?? derivedValue

        return (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 1,
                border: '1.5px solid',
                borderColor: error ? 'error.main' : 'divider',
                overflow: 'hidden',
              }}
            >
              <TextField
                value={displayValue}
                onChange={(e) => {
                  const raw = e.target.value
                  setDraft(raw)
                  if (raw === '') {
                    field.onChange('')
                    return
                  }
                  const entered = Number(raw)
                  if (Number.isNaN(entered)) return
                  const storedValue = toStored(entered, unit, converter)
                  field.onChange(storedValue === null ? raw : String(storedValue))
                }}
                onBlur={() => {
                  field.onBlur()
                  setDraft(null)
                }}
                variant="standard"
                placeholder={placeholder}
                error={!!error}
                fullWidth
                disabled={disabled}
                InputProps={{ disableUnderline: true }}
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: 18,
                  fontWeight: 600,
                  bgcolor: disabled ? 'grey.200' : 'common.white',
                }}
              />
              {endAdornment}
            </Box>
            {error && (
              <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
                {error}
              </Typography>
            )}
          </Box>
        )
      }}
    />
  )
}

const GridTable = ({ isDialog, isEdit }: GridTableProps) => {
  const { t } = useTranslation('shrimpSales')
  const speciesName = useSpeciesName()
  const isShrimp = useIsShrimpSpecie()

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext()
  const categories = useWatch({ control, name: 'categories' }) as ShrimpCategoryRecord[]
  const shrimpPerKg = useWatch({ control, name: 'shrimpPerKg' }) as string

  const unitSuffix = speciesName === SpeciesName.Fish ? t('gram.per.one') : t('shrimp.per.kg')
  const converter = buildSaleUnitConverter(isShrimp, shrimpPerKg, unitSuffix)

  const getSubCategoryName = ({
    index,
    subIndex,
    field,
  }: {
    index: number
    subIndex: number
    field: string
  }) => `categories[${index}].subCategories[${subIndex}].${field}`

  const handleAddCategory = () => {
    setValue('categories', [
      ...categories,
      {
        id: Date.now(),
        name: t('new.type'),
        subCategories: [{ id: Date.now() + 1, name: '-', price: '', amount: '' }],
      },
    ])
  }

  const handleAddSubCategory = (categoryIdx: number) => {
    setValue(
      'categories',
      categories.map((cat, i) =>
        i === categoryIdx
          ? {
              ...cat,
              subCategories: [
                ...cat.subCategories,
                { id: Date.now(), name: '-', price: '', amount: '' },
              ],
            }
          : cat
      )
    )
  }

  const handleRemoveCategory = (idx: number) => {
    setValue(
      'categories',
      categories.filter((_, index) => index !== idx)
    )
  }

  const handleRemoveSubCategory = (categoryIdx: number, subIndex: number) => {
    setValue(
      'categories',
      categories.map((cat, i) =>
        i === categoryIdx
          ? { ...cat, subCategories: cat.subCategories.filter((_, si) => si !== subIndex) }
          : cat
      )
    )
  }

  const isInputDisabled = !isDialog && !isEdit

  return (
    <Stack spacing={3}>
      {(categories ?? []).map((category, index) => {
        const categoryName = `categories[${index}].name`

        return (
          <Box key={String(category.id ?? index)}>
            <Box sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                {isDialog || isEdit ? (
                  <Controller
                    name={categoryName}
                    control={control}
                    render={({ field }) => (
                      <EditableField
                        {...field}
                        hidePencil={isInputDisabled}
                        placeholder={t('input.type.placeholder')}
                        sx={{ fontSize: '1.25rem', fontWeight: 700 }}
                      />
                    )}
                  />
                ) : (
                  <Typography variant="h6" fontWeight={700}>
                    {category.name}
                  </Typography>
                )}

                {(isDialog || isEdit) && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveCategory(index)}
                    disableRipple
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Box>

            <Box
              sx={{
                borderRadius: 2,
                bgcolor: 'grey.50',
                p: 2,
              }}
            >
              <Stack spacing={2}>
                {category.subCategories.map((sub, subIndex) => {
                  const amountName = getSubCategoryName({
                    index,
                    subIndex,
                    field: 'amount',
                  })
                  const priceName = getSubCategoryName({
                    index,
                    subIndex,
                    field: 'price',
                  })
                  const subCategoryName = getSubCategoryName({
                    index,
                    subIndex,
                    field: 'name',
                  })

                  const amountError = errorMessageByField(amountName, errors)
                  const priceError = errorMessageByField(priceName, errors)
                  const nameError = errorMessageByField(subCategoryName, errors)

                  const unit: SaleQuantityUnit = sub.unit ?? defaultUnit(converter)
                  const storedKg = Number(sub.amount) || undefined
                  const rowTotal =
                    storedKg !== undefined && sub.price ? storedKg * Number(sub.price) : undefined
                  const approx =
                    storedKg !== undefined ? approxLabel(storedKg, unit, converter) : null
                  const hint = unit === 'piece' ? t('hint.enter.piece') : t('hint.enter.weight')

                  return (
                    <Box
                      key={String(sub.id ?? `${index}-${subIndex}`)}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        p: 2,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                      >
                        {isDialog || isEdit ? (
                          <Controller
                            name={subCategoryName}
                            control={control}
                            render={({ field }) => (
                              <EditableField
                                {...field}
                                hidePencil={isInputDisabled}
                                placeholder={t('input.type.placeholder')}
                                error={!!nameError}
                                helperText={nameError}
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                          />
                        ) : (
                          <Typography fontWeight={600}>{sub.name}</Typography>
                        )}

                        {(isEdit || isDialog) && (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveSubCategory(index, subIndex)}
                            disableRipple
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>

                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={4}>
                          <Typography variant="body2" color="text.secondary" mb={0.5}>
                            {unit === 'kg' ? t('weight') : t('amount')}
                          </Typography>
                          <ConvertedField
                            name={amountName}
                            unit={unit}
                            converter={converter}
                            control={control}
                            placeholder={t('amount.placeholder')}
                            error={amountError}
                            disabled={isInputDisabled}
                            toStored={toKg}
                            fromStored={fromKg}
                            endAdornment={
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  px: 2,
                                  bgcolor: 'grey.200',
                                  borderLeft: '1px solid',
                                  borderColor: 'grey.300',
                                  alignSelf: 'stretch',
                                }}
                              >
                                <Select
                                  variant="standard"
                                  disableUnderline
                                  value={unit}
                                  disabled={isInputDisabled}
                                  onChange={(e) =>
                                    setValue(
                                      `categories[${index}].subCategories[${subIndex}].unit`,
                                      e.target.value as SaleQuantityUnit
                                    )
                                  }
                                  sx={{
                                    background: 'transparent',
                                    fontSize: 18,
                                  }}
                                >
                                  <MenuItem value="piece">{t('unit.piece')}</MenuItem>
                                  <MenuItem value="kg">{t('unit.kg')}</MenuItem>
                                </Select>
                              </Box>
                            }
                          />
                        </Grid>

                        <Grid item xs={6} sm={4}>
                          <Typography variant="body2" color="text.secondary" mb={0.5}>
                            {t('price')}
                          </Typography>
                          <ConvertedField
                            name={priceName}
                            unit={unit}
                            converter={converter}
                            control={control}
                            placeholder={t('price.placeholder')}
                            error={priceError}
                            disabled={isInputDisabled}
                            toStored={toPricePerKg}
                            fromStored={fromPricePerKg}
                            endAdornment={
                              <Typography
                                color="text.secondary"
                                noWrap
                                sx={{ flexShrink: 0, px: 2 }}
                              >
                                {unit === 'piece' ? t('baht.per.piece') : t('baht.per.kg')}
                              </Typography>
                            }
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary" mb={0.5} align="right">
                            {t('row.total')}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              border: '1.5px solid transparent',
                              borderRadius: 1,
                              px: 2,
                              py: 1.75,
                            }}
                          >
                            <Typography
                              fontSize={18}
                              fontWeight={rowTotal !== undefined ? 700 : 400}
                              color={rowTotal !== undefined ? 'primary.main' : 'text.disabled'}
                              whiteSpace="nowrap"
                            >
                              ฿{fNumber(rowTotal ?? 0)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {(approx || isDialog || isEdit) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: 'block' }}
                        >
                          {approx ?? hint}
                        </Typography>
                      )}
                    </Box>
                  )
                })}
              </Stack>

              {(isEdit || isDialog) && (
                <Box mt={1}>
                  <AddButton
                    startIcon={<AddIcon />}
                    onClick={() => handleAddSubCategory(index)}
                    disableRipple
                  >
                    {t('add.sub.category')}
                  </AddButton>
                </Box>
              )}
            </Box>
          </Box>
        )
      })}

      {(isDialog || isEdit) && (
        <AddCategoryButton onClick={handleAddCategory} disableRipple>
          <Stack direction="row" spacing={1.5} alignItems="center" width="100%">
            <Box
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: 1.5,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AddIcon fontSize="small" />
            </Box>
            <Stack alignItems="flex-start">
              <Typography fontWeight={700} color="text.primary">
                {t('add.type')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isShrimp ? t('add.type.example.shrimp') : t('add.type.example')}
              </Typography>
            </Stack>
          </Stack>
        </AddCategoryButton>
      )}
    </Stack>
  )
}

export default GridTable
